import { useShallow } from 'zustand/react/shallow';

import { SHOULD_GET_HG_DATA } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { ProviderType } from '../../models/ProviderDetails';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { getProviderLocationWithProviderGroup } from '../../utils/providerDetails.utils';
import {
  getNameAttrs,
  parseProviderName,
} from '../../utils/providerNames.utils';
import { useFeatureFlag } from '../useFeatureFlag';
import { useProviderDetails } from '../useProviderDetails';

export const useChangePCPData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const providerTypeFromParams = (urlParams.get('providerType') ||
    '') as ProviderType;
  const providerId = urlParams.get('choosePCPId') || '';
  const currentSelectedId = urlParams.get('currentSelectedId') || '';
  const selectedNetworkAffiliationId =
    urlParams.get('selectedNetworkAffiliationId') || '';
  const [enablePCPParamsFromURL] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PCP_PARAMS_FROM_URL,
  ]);

  const { data: providerDetailsData } = useProviderDetails({
    providerType: providerTypeFromParams,
    providerId,
    shouldGetHGData: SHOULD_GET_HG_DATA,
  });

  const {
    setPCPSearchState,
    dependentSeqNbr: storeDependentSeqNbr,
    formattedProviderName: storeFormattedProviderName,
    pcpEffectiveDate: storePcpEffectiveDate,
    selectedLocation: storeSelectedLocation,
    selectedNetworkAffiliationId: storeSelectedNetworkAffiliationId,
    providerType: storeProviderType,
    primarySpeciality: storePrimarySpeciality,
    imageLocation: storeImageLocation,
  } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      formattedProviderName: state.pcpSearchState.formattedProviderName,
      pcpEffectiveDate: state.pcpSearchState.pcpEffectiveDate,
      selectedLocation: state.pcpSearchState.selectedLocation,
      selectedNetworkAffiliationId:
        state.pcpSearchState.selectedNetworkAffiliationId,
      providerType: state.pcpSearchState.providerType,
      primarySpeciality: state.pcpSearchState.primarySpeciality,
      imageLocation: state.pcpSearchState.imageLocation,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
      setPCPSearchState: state.setPCPSearchState,
    }))
  );

  let result = {
    setPCPSearchState,
    dependentSeqNbr: storeDependentSeqNbr,
    formattedProviderName: storeFormattedProviderName,
    pcpEffectiveDate: storePcpEffectiveDate,
    selectedLocation: storeSelectedLocation,
    selectedNetworkAffiliationId: storeSelectedNetworkAffiliationId,
    providerType: storeProviderType,
    primarySpeciality: storePrimarySpeciality,
    imageLocation: storeImageLocation,
  };

  if (enablePCPParamsFromURL && providerDetailsData) {
    const providerDetails =
      providerDetailsData?.facilityDetails?.providerDetails ||
      providerDetailsData?.providerDetails?.providerDetails;

    const providerLocations = getProviderLocationWithProviderGroup(
      providerDetails?.providerLocations || []
    );

    const selectedLocationIndex = providerLocations.findIndex(
      (location) => location.locationId === currentSelectedId
    );

    const selectedLocation =
      selectedLocationIndex !== -1
        ? providerLocations[selectedLocationIndex]
        : providerLocations[0] || null;

    const {
      providerName = '',
      professionalDesignation = '',
      specialties = [],
      primaryDegrees = [],
      imageLocation = '',
    } = providerDetails || {};

    const nameAttrs = getNameAttrs(providerDetails);
    const formattedProviderName = parseProviderName(
      providerName,
      providerTypeFromParams,
      professionalDesignation,
      nameAttrs
    );

    const fullProviderName = primaryDegrees?.length
      ? `${formattedProviderName}, ${primaryDegrees[0]}`
      : formattedProviderName;
    const selectedLocationString = selectedLocation
      ? JSON.stringify(selectedLocation)
      : null;

    result = {
      ...result,
      dependentSeqNbr: providerDetails?.dependentSeqNbr || null,
      formattedProviderName: fullProviderName,
      pcpEffectiveDate: providerDetails?.pcpEffectiveDate || null,
      selectedLocation: selectedLocationString,
      providerType: providerTypeFromParams || storeProviderType,
      primarySpeciality: specialties[0] || null,
      imageLocation,
      selectedNetworkAffiliationId,
    };
  }

  return result;
};
